import React, { useEffect, useState, useCallback } from "react";
import { Form, Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { ROLES } from "../../../model/roles";
import SafetyAgenzieService from "../../../services/safety/SafetyAgenzieService";

/**
 * AgenziaSelector
 *
 * Selettore di "contesto agenzia" per il modulo Adempimenti Sicurezza.
 * Visibile SOLO ai manager (Admin / Superpartner / Developer / Course designer):
 *  - "Tutte le agenzie" => vista globale aggregata (nessun filtro)
 *  - un'agenzia specifica (inclusa High Educational come ente) => si vedono e si
 *    gestiscono solo i suoi dati di compliance.
 *
 * La scelta viene persistita in localStorage cosi' resta coerente tra
 * Dashboard / Aziende / Lavoratori.
 */

export const SAFETY_CTX_KEY = "safety_ctx_id_agenzia";

const MANAGER_ROLES = [
    ROLES.SUPERADMIN,
    ROLES.ADMIN,
    ROLES.DEVELOPER,
    ROLES.COURSE_DESIGNER,
    ROLES.SUPERPARTNER,
];

export function isSafetyManager(role) {
    return MANAGER_ROLES.includes(Number(role));
}

export function getSafetyCtxAgenzia() {
    const v = localStorage.getItem(SAFETY_CTX_KEY);
    return v && v !== "" ? Number(v) : "";
}

/**
 * Hook per leggere/scrivere il contesto agenzia.
 * Ritorna { idAgenzia, setIdAgenzia, isManager }.
 * Per i non-manager idAgenzia resta sempre "" (il backend forza gia' il tenant).
 */
export function useSafetyAgenziaContext() {
    const role = useSelector((state) => state.user?.userData?.role);
    const manager = isSafetyManager(role);
    const [idAgenzia, setId] = useState(() => (manager ? getSafetyCtxAgenzia() : ""));

    const setIdAgenzia = useCallback((val) => {
        const norm = val === "" || val === null || val === undefined ? "" : Number(val);
        if (norm === "") {
            localStorage.removeItem(SAFETY_CTX_KEY);
        } else {
            localStorage.setItem(SAFETY_CTX_KEY, String(norm));
        }
        setId(norm);
    }, []);

    return { idAgenzia, setIdAgenzia, isManager: manager };
}

export default function AgenziaSelector({ idAgenzia, onChange, className }) {
    const role = useSelector((state) => state.user?.userData?.role);
    const manager = isSafetyManager(role);
    const [agenzie, setAgenzie] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!manager) return undefined;
        let mounted = true;
        (async () => {
            setLoading(true);
            try {
                const resp = await SafetyAgenzieService.getAll();
                const data = resp && typeof resp.json === "function" ? await resp.json() : resp;
                if (mounted) setAgenzie(Array.isArray(data) ? data : []);
            } catch (_e) {
                if (mounted) setAgenzie([]);
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, [manager]);

    if (!manager) return null;

    return (
        <div className={`d-flex align-items-center gap-2 ${className || ""}`}>
            <span className="text-muted small text-nowrap">
                <i className="bi bi-diagram-3 me-1" />
                Contesto
            </span>
            <Form.Select
                size="sm"
                style={{ minWidth: 230 }}
                value={idAgenzia === "" || idAgenzia == null ? "" : String(idAgenzia)}
                onChange={(e) => onChange && onChange(e.target.value)}
                disabled={loading}
                title="Agenzia di cui gestire la compliance ('Tutte' = vista globale)"
            >
                <option value="">Tutte le agenzie (globale)</option>
                {agenzie.map((a) => (
                    <option key={a.id_agenzia} value={a.id_agenzia}>
                        {a.denominazione}
                        {Number(a.safety_enabled) === 1 ? "" : " — GAS off"}
                    </option>
                ))}
            </Form.Select>
            {loading && <Spinner size="sm" animation="border" />}
        </div>
    );
}
